/**
 * Session type recommendation logic based on favorited scenes from the
 * Scene Inspiration Modal (SY27 paired recommendation spec).
 */
import { SessionType, type SessionTypeKey } from '@/stores/sessionType'

export enum SceneBucket {
  Studio = 'studio',
  Outdoor = 'outdoor',
  Destination = 'destination'
}

/** Maps scene inspiration category identifiers to recommendation buckets. */
export const CATEGORY_BUCKET: Record<string, SceneBucket> = {
  'elite-unlimited-outdoor': SceneBucket.Outdoor,
  'outdoor-destinations': SceneBucket.Destination,
  'studio-and-school': SceneBucket.Studio
}

/** Default recommendation when no scenes are favorited. */
export const defaultRecommendedSessionTypeKeys: SessionTypeKey[] = [SessionType.Elite]

/**
 * Counts favorited scenes by their recommendation bucket.
 * Unrecognized IDs (not in the map) are ignored.
 */
function countByBucket(
  favoritedSceneIds: string[],
  sceneToBucket: Record<string, SceneBucket>
): Record<SceneBucket, number> {
  const counts: Record<SceneBucket, number> = {
    [SceneBucket.Studio]: 0,
    [SceneBucket.Outdoor]: 0,
    [SceneBucket.Destination]: 0
  }
  for (const id of favoritedSceneIds) {
    const bucket = sceneToBucket[id]
    if (bucket) counts[bucket]++
  }
  return counts
}

/**
 * Computes the recommended session type keys based on favorited scene IDs.
 * Returns up to 2 recommendations.
 *
 * SY27 recommendation rules (2 recommendations):
 *   2  studio                           → Most Popular + Legacy
 *   3  studio                           → Elite + Most Popular
 *   4  studio + outdoor                 → Elite Plus + Elite
 *   5+ studio + outdoor                 → Elite Unlimited + Elite Plus
 *   5+ studio &/or destination          → Elite Unlimited
 *   (fallback)                          → Elite (default)
 *
 * @param favoritedSceneIds - Scene IDs the user favorited in the inspiration modal.
 * @param sceneToBucket - Lookup from scene ID → recommendation bucket. Built by
 *   the caller from CMS scene inspiration data (category → bucket mapping).
 */
export function getRecommendedSessionTypeKeys(
  favoritedSceneIds: string[],
  sceneToBucket: Record<string, SceneBucket>
): SessionTypeKey[] {
  const counts = countByBucket(favoritedSceneIds, sceneToBucket)
  const studioCount = counts[SceneBucket.Studio]
  const outdoorCount = counts[SceneBucket.Outdoor]
  const destinationCount = counts[SceneBucket.Destination]
  const hasOutdoor = outdoorCount > 0
  const totalStudioAndDestination = studioCount + destinationCount
  // 5+ studio &/or destination (no outdoor)
  if (totalStudioAndDestination >= 5 && !hasOutdoor) {
    return [SessionType.EliteUnlimited]
  }
  // 5+ studio + outdoor
  if (studioCount >= 5 && hasOutdoor) {
    return [SessionType.EliteUnlimited, SessionType.ElitePlus]
  }
  // 4 studio + outdoor
  if (studioCount >= 4 && hasOutdoor) {
    return [SessionType.ElitePlus, SessionType.Elite]
  }
  // 3 studio
  if (studioCount >= 3) {
    return [SessionType.Elite, SessionType.MostPopular]
  }
  // 2 studio
  if (studioCount >= 2) {
    return [SessionType.MostPopular, SessionType.Legacy]
  }
  // Fallback: default to Elite
  return [SessionType.Elite]
}

/**
 * Builds a scene-to-bucket lookup map from scene inspiration modal data.
 * Each scene inherits its parent category's bucket via the provided category map.
 *
 * @param categories - Scene inspiration categories with their scene children.
 * @param categoryBuckets - Maps category identifier → recommendation bucket.
 */
export function buildSceneToBucketMap(
  categories: { identifier: string; scenes: { sceneId: string }[] }[],
  categoryBuckets: Record<string, SceneBucket>
): Record<string, SceneBucket> {
  const map: Record<string, SceneBucket> = {}
  for (const cat of categories) {
    const bucket = categoryBuckets[cat.identifier]
    if (!bucket) continue
    for (const scene of cat.scenes) {
      map[scene.sceneId] = bucket
    }
  }
  return map
}
